/**
 * NavigatorX Waitlist Service
 * 
 * Comprehensive waitlist management:
 * - Entry management (CRUD)
 * - Invite sending (single & bulk)
 * - State-based launch control
 * - Statistics and analytics
 * - Export functionality
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.1 - Audit fixes applied
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { navDb } from '../firebase/client';
import { logAdminAction } from './navigatorXAuditService';

// ============================================================================
// CONSTANTS
// ============================================================================

const WAITLIST_COLLECTION = 'waitlist';
const LAUNCH_CONFIG_COLLECTION = 'nav_config';
const LAUNCH_CONFIG_DOC_ID = 'launch_states';

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Validate email format
 */
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate admin ID - throws if invalid
 */
function validateAdminId(adminId: string): void {
  if (!adminId || adminId === 'unknown' || adminId.trim() === '') {
    throw new Error('Valid admin authentication required for this operation');
  }
}

/**
 * Sanitize string input to prevent XSS
 */
function sanitizeInput(input: string): string {
  if (!input) return '';
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .trim();
}

// ============================================================================
// TYPES
// ============================================================================

export interface WaitlistEntry {
  id?: string;
  email: string;
  state?: string;
  source?: string;
  referralCode?: string;
  createdAt: Timestamp;
  invitedAt?: Timestamp;
  invitedBy?: string;
  convertedAt?: Timestamp;
  convertedUserId?: string;
}

export interface WaitlistStats {
  total: number;
  pending: number;
  invited: number;
  converted: number;
  conversionRate: number;
  byState: Record<string, number>;
  bySource: Record<string, number>;
  todaySignups: number;
  weekSignups: number;
}

// ============================================================================
// CRUD OPERATIONS
// ============================================================================

/**
 * Get all waitlist entries
 */
export async function getWaitlistEntries(): Promise<WaitlistEntry[]> {
  try {
    const q = query(collection(navDb, WAITLIST_COLLECTION), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as WaitlistEntry[];
  } catch (error) {
    console.error('Error fetching waitlist entries:', error);
    throw new Error('Failed to fetch waitlist entries');
  }
}

/**
 * Get a single waitlist entry by ID
 */
export async function getWaitlistEntry(id: string): Promise<WaitlistEntry | null> {
  if (!id) return null;
  
  try {
    const docSnap = await getDoc(doc(navDb, WAITLIST_COLLECTION, id));
    if (!docSnap.exists()) return null;
    return { id: docSnap.id, ...docSnap.data() } as WaitlistEntry;
  } catch (error) {
    console.error('Error fetching waitlist entry:', error);
    return null;
  }
}

/**
 * Get waitlist entry by email
 */
export async function getWaitlistByEmail(email: string): Promise<WaitlistEntry | null> {
  if (!email) return null;
  
  try {
    const normalizedEmail = email.toLowerCase().trim();
    const q = query(collection(navDb, WAITLIST_COLLECTION), where('email', '==', normalizedEmail));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) return null;
    const docData = snapshot.docs[0];
    return { id: docData.id, ...docData.data() } as WaitlistEntry;
  } catch (error) {
    console.error('Error fetching waitlist by email:', error);
    return null;
  }
}

/**
 * Add a new waitlist entry
 */
export async function addWaitlistEntry(
  email: string,
  state?: string,
  source?: string,
  referralCode?: string
): Promise<string> {
  // Validate email
  if (!email || !isValidEmail(email)) {
    throw new Error('Invalid email address');
  }
  
  const sanitizedEmail = email.toLowerCase().trim();
  
  // Check if already exists
  const existing = await getWaitlistByEmail(sanitizedEmail);
  if (existing) {
    throw new Error('Email already on waitlist');
  }
  
  try {
    const docRef = await addDoc(collection(navDb, WAITLIST_COLLECTION), {
      email: sanitizedEmail,
      state: state ? sanitizeInput(state) : null,
      source: source ? sanitizeInput(source) : null,
      referralCode: referralCode ? sanitizeInput(referralCode) : null,
      createdAt: Timestamp.now()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error adding waitlist entry:', error);
    throw new Error('Failed to add to waitlist');
  }
}

/**
 * Delete a waitlist entry
 */
export async function deleteWaitlistEntry(id: string, adminId: string): Promise<void> {
  validateAdminId(adminId);
  
  if (!id) {
    throw new Error('Entry ID is required');
  }
  
  try {
    await deleteDoc(doc(navDb, WAITLIST_COLLECTION, id));
    await logAdminAction(adminId, 'DELETE_WAITLIST_ENTRY', id);
  } catch (error) {
    console.error('Error deleting waitlist entry:', error);
    throw new Error('Failed to delete waitlist entry');
  }
}

// ============================================================================
// INVITE OPERATIONS
// ============================================================================

/**
 * Send an invite to a waitlist entry
 */
export async function sendInvite(id: string, adminId: string): Promise<void> {
  validateAdminId(adminId);
  
  if (!id) {
    throw new Error('Entry ID is required');
  }
  
  const entry = await getWaitlistEntry(id);
  if (!entry) throw new Error('Entry not found');
  if (entry.invitedAt) throw new Error('Already invited');
  
  try {
    // TODO: Integrate with email service (SendGrid, Mailgun, etc.)
    // For now, just mark as invited
    
    await updateDoc(doc(navDb, WAITLIST_COLLECTION, id), {
      invitedAt: Timestamp.now(),
      invitedBy: adminId
    });
    
    await logAdminAction(adminId, 'SEND_WAITLIST_INVITE', id, { email: entry.email });
  } catch (error) {
    console.error('Error sending invite:', error);
    throw new Error('Failed to send invite');
  }
}

/**
 * Send bulk invites
 */
export async function sendBulkInvites(ids: string[], adminId: string): Promise<{ success: number; failed: number }> {
  validateAdminId(adminId);
  
  if (!ids || ids.length === 0) {
    throw new Error('No entries selected');
  }
  
  let success = 0;
  let failed = 0;
  
  for (const id of ids) {
    try {
      await sendInvite(id, adminId);
      success++;
    } catch (error) {
      failed++;
    }
  }
  
  await logAdminAction(adminId, 'BULK_WAITLIST_INVITE', 'bulk', { 
    total: ids.length, 
    success, 
    failed 
  });
  
  return { success, failed };
}

/**
 * Mark entry as converted (user signed up)
 */
export async function markConverted(
  waitlistId: string,
  userId: string
): Promise<void> {
  if (!waitlistId || !userId) {
    throw new Error('Waitlist ID and User ID are required');
  }
  
  try {
    await updateDoc(doc(navDb, WAITLIST_COLLECTION, waitlistId), {
      convertedAt: Timestamp.now(),
      convertedUserId: userId
    });
  } catch (error) {
    console.error('Error marking as converted:', error);
    throw new Error('Failed to mark as converted');
  }
}

// ============================================================================
// STATISTICS
// ============================================================================

/**
 * Get waitlist statistics
 */
export async function getWaitlistStats(): Promise<WaitlistStats> {
  try {
    const entries = await getWaitlistEntries();
    
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart = new Date(todayStart);
    weekStart.setDate(weekStart.getDate() - 7);
    
    const byState: Record<string, number> = {};
    const bySource: Record<string, number> = {};
    
    let pending = 0;
    let invited = 0;
    let converted = 0;
    let todaySignups = 0;
    let weekSignups = 0;
    
    for (const entry of entries) {
      // Count by status
      if (entry.convertedAt) {
        converted++;
      } else if (entry.invitedAt) {
        invited++;
      } else {
        pending++;
      }
      
      // Count by state
      const state = entry.state || 'Unknown';
      byState[state] = (byState[state] || 0) + 1;
      
      // Count by source
      const source = entry.source || 'Direct';
      bySource[source] = (bySource[source] || 0) + 1;
      
      // Recent signups
      if (entry.createdAt) {
        const createdDate = entry.createdAt.toDate();
        if (createdDate >= todayStart) {
          todaySignups++;
        }
        if (createdDate >= weekStart) {
          weekSignups++;
        }
      }
    }
    
    const total = entries.length;
    const conversionRate = invited > 0 ? Math.round((converted / invited) * 100) : 0;
    
    return {
      total,
      pending,
      invited,
      converted,
      conversionRate,
      byState,
      bySource,
      todaySignups,
      weekSignups
    };
  } catch (error) {
    console.error('Error calculating waitlist stats:', error);
    throw new Error('Failed to calculate waitlist statistics');
  }
}

// ============================================================================
// LAUNCH CONTROL
// ============================================================================

/**
 * Get list of launched states
 */
export async function getLaunchedStates(): Promise<string[]> {
  try {
    const docSnap = await getDoc(doc(navDb, LAUNCH_CONFIG_COLLECTION, LAUNCH_CONFIG_DOC_ID));
    
    if (!docSnap.exists()) {
      return ['OK']; // Default to Oklahoma
    }
    
    const data = docSnap.data();
    return Array.isArray(data.launchedStates) ? data.launchedStates : ['OK'];
  } catch (error) {
    console.error('Error fetching launched states:', error);
    return ['OK']; // Return default on error
  }
}

/**
 * Set state launch status
 */
export async function setStateLaunchStatus(
  stateCode: string,
  launched: boolean,
  adminId: string
): Promise<void> {
  validateAdminId(adminId);
  
  if (!stateCode || stateCode.trim() === '') {
    throw new Error('State code is required');
  }
  
  const normalizedStateCode = stateCode.toUpperCase().trim();
  
  try {
    const current = await getLaunchedStates();
    
    let updated: string[];
    if (launched) {
      updated = [...new Set([...current, normalizedStateCode])];
    } else {
      updated = current.filter(s => s !== normalizedStateCode);
    }
    
    await setDoc(doc(navDb, LAUNCH_CONFIG_COLLECTION, LAUNCH_CONFIG_DOC_ID), {
      launchedStates: updated,
      updatedAt: Timestamp.now(),
      updatedBy: adminId
    });
    
    await logAdminAction(adminId, launched ? 'LAUNCH_STATE' : 'UNLAUNCH_STATE', normalizedStateCode);
    
    // If launching, auto-invite users from that state
    if (launched) {
      const entries = await getWaitlistEntries();
      const stateEntries = entries.filter(e => 
        e.state?.toUpperCase() === normalizedStateCode && !e.invitedAt
      );
      
      if (stateEntries.length > 0) {
        // Use Promise.allSettled to not fail on individual invite errors
        const inviteIds = stateEntries.map(e => e.id!).filter(Boolean);
        if (inviteIds.length > 0) {
          await sendBulkInvites(inviteIds, adminId);
        }
      }
    }
  } catch (error) {
    console.error('Error setting state launch status:', error);
    throw new Error('Failed to update state launch status');
  }
}

/**
 * Check if a state is launched
 */
export async function isStateLaunched(stateCode: string): Promise<boolean> {
  if (!stateCode) return false;
  
  const launched = await getLaunchedStates();
  return launched.includes(stateCode.toUpperCase().trim());
}

// ============================================================================
// EXPORT
// ============================================================================

/**
 * Export waitlist to CSV
 */
export function exportWaitlist(entries: WaitlistEntry[]): string {
  if (!entries || entries.length === 0) {
    return 'Email,State,Source,Signed Up,Invited,Converted\n';
  }
  
  const headers = ['Email', 'State', 'Source', 'Signed Up', 'Invited', 'Converted'];
  
  const rows = entries.map(entry => [
    entry.email || '',
    entry.state || '',
    entry.source || '',
    entry.createdAt?.toDate?.()?.toISOString() || '',
    entry.invitedAt?.toDate?.()?.toISOString() || '',
    entry.convertedAt?.toDate?.()?.toISOString() || ''
  ]);
  
  return [
    headers.join(','),
    ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
  ].join('\n');
}

// ============================================================================
// BULK OPERATIONS
// ============================================================================

/**
 * Import waitlist from array of emails
 */
export async function bulkImportWaitlist(
  emails: string[],
  state?: string,
  source?: string
): Promise<{ success: number; duplicates: number; errors: number }> {
  if (!emails || emails.length === 0) {
    return { success: 0, duplicates: 0, errors: 0 };
  }
  
  let success = 0;
  let duplicates = 0;
  let errors = 0;
  
  for (const email of emails) {
    try {
      await addWaitlistEntry(email, state, source);
      success++;
    } catch (error: any) {
      if (error.message === 'Email already on waitlist') {
        duplicates++;
      } else if (error.message === 'Invalid email address') {
        errors++;
      } else {
        errors++;
      }
    }
  }
  
  return { success, duplicates, errors };
}
